import type { SearchSelectionEvent } from '@/composables/useNewSearchSuggest';
import type { EventBusKey } from '@vueuse/core';
import type { CurrentPage } from '~/types/mixpanel';

const regionChangedBusKey: EventBusKey<boolean> = Symbol('region-changed');

const autoZoomBusKey: EventBusKey<number> = Symbol('auto-zoom');

const propertyListPaginationBusKey: EventBusKey<string> = Symbol(
	'property-list-pagination'
);

const propertyCardUnhoverBusKey: EventBusKey<string> = Symbol(
	'property-card-unhover'
);

const propertyCardHoverBusKey: EventBusKey<string> = Symbol(
	'property-card-hover'
);

const viewMoreClickedBusKey: EventBusKey<CurrentPage> =
	Symbol('view-more-clicked');

const desktopHeroSectionPhotoCountBusKey: EventBusKey<number | undefined> =
	Symbol('desktop-hero-section-photo-count');

const mobileHeroSectionPhotoCountBusKey: EventBusKey<number | undefined> =
	Symbol('mobile-hero-section-photo-count');

const openContactModalAtOtpBusKey: EventBusKey<void> = Symbol(
	'open-contact-modal-at-otp'
);

const similarPropertiesClickBusKey: EventBusKey<{
	origin: 'floating' | 'card' | 'fixed';
	commercialId: string;
}> = Symbol('similar-properties-click');

const contactBrokerClickBusKey: EventBusKey<{
	commercialId: string;
	askingPrice: number;
	companyName: string;
	agentName: string;
}> = Symbol('contact-broker-click');

const isMenuBeingShownSymbol = Symbol('is-menu-being-shown');

/** Layout Shorts: menu mobile no `body` para stacking acima da FiltersBar. */
const shortsMobileNavTeleportSymbol = Symbol('shorts-mobile-nav-teleport');

const wishlistUpdatedBusKey: EventBusKey<{
	propertyId: string;
	action: 'added' | 'removed';
	wishlistId?: string;
}> = Symbol('wishlist:updated');

const searchSuggestSelectionBusKey: EventBusKey<SearchSelectionEvent> = Symbol(
	'search-suggest-selection'
);

export const EventBusKeys = {
	regionChanged: regionChangedBusKey,
	autoZoom: autoZoomBusKey,
	propertyListPagination: propertyListPaginationBusKey,
	propertyCardUnhover: propertyCardUnhoverBusKey,
	propertyCardHover: propertyCardHoverBusKey,
	viewMoreClicked: viewMoreClickedBusKey,
	desktopHeroSectionPhotoCount: desktopHeroSectionPhotoCountBusKey,
	mobileHeroSectionPhotoCount: mobileHeroSectionPhotoCountBusKey,
	openContactModalAtOtp: openContactModalAtOtpBusKey,
	similarPropertiesClick: similarPropertiesClickBusKey,
	contactBrokerClick: contactBrokerClickBusKey,
	wishlistUpdated: wishlistUpdatedBusKey,
	searchSuggestSelection: searchSuggestSelectionBusKey
} as const;

export const DependencyInjectionSymbols = {
	isMenuBeingShown: isMenuBeingShownSymbol,
	shortsMobileNavTeleportToBody: shortsMobileNavTeleportSymbol
} as const;

export type EventBusKeyType = (typeof EventBusKeys)[keyof typeof EventBusKeys];
export type EventBusPayload<T extends EventBusKeyType> =
	T extends EventBusKey<infer U> ? U : never;
